import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CreditCard,
  Receipt,
  Calculator,
  FileText,
  TrendingUp,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { employeesAPI } from "@/lib/api";
import { exportWireTransferOrder } from "@/services/pdfExport.service";

import PayrollCalculator from "./PayrollCalculator";
import PayslipManagement from "./PayslipManagement";
import PayslipGenerator from "./PayslipGenerator";
import ExpenseManagement from "./ExpenseManagement";
import PayrollReports from "./PayrollReports";
import { authAPI } from "@/lib/api";

export default function PayrollPage() {
  const [activeTab, setActiveTab] = useState("payslips");
  const [currentUser, setCurrentUser] = useState(null);
  const [wireTransferLoading, setWireTransferLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    getUser();
  }, []);

  const getUser = () => {
    authAPI
      .getCurrentUser()
      .then((response) => {
        const user = response.data.data.user;
        setCurrentUser(user);
      })
      .catch((error) => {
        console.error("Failed to fetch current user:", error);
      });
  };

  // Handle wire transfer order generation
  const handleWireTransfer = async () => {
    try {
      setWireTransferLoading(true);
      
      // Fetch all active employees
      const params = new URLSearchParams();
      params.append("statut", "actif");
      params.append("limit", "1000");
      
      const response = await employeesAPI.getAll(params);
      const activeEmployees = response.data.data.employees;
      
      // Filter only active employees
      const validEmployees = activeEmployees.filter(
        emp => emp.statut_employe === "actif"
      );
      
      if (validEmployees.length === 0) {
        toast({
          title: "Attention",
          description: "Aucun employé actif trouvé",
          variant: "destructive",
        });
        return;
      }
      
      await exportWireTransferOrder(validEmployees);
      
      // Count employees with missing data
      const missingIban = validEmployees.filter(emp => !emp.iban).length;
      const missingSalary = validEmployees.filter(emp => !emp.salaire || parseFloat(emp.salaire) <= 0).length;
      
      let description = `Ordre de virement généré avec succès pour ${validEmployees.length} employé(s)`;
      if (missingIban > 0 || missingSalary > 0) {
        description += ` (Attention: ${missingIban} sans RIB, ${missingSalary} sans salaire)`;
      }
      
      toast({
        title: "Succès",
        description: description,
      });
    } catch (error) {
      console.error("Wire transfer error:", error);
      toast({
        title: "Erreur",
        description: error.message || "Erreur lors de la génération de l'ordre de virement",
        variant: "destructive",
      });
    } finally {
      setWireTransferLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Gestion de la Paie
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Gérez les bulletins de paie, notes de frais et calculs salariaux
              </p>
            </div>
          </div>
          
          {/* Wire Transfer Button */}
          {currentUser && (currentUser.role === "admin" || currentUser.role === "rh") && (
            <Button
              onClick={handleWireTransfer}
              disabled={wireTransferLoading}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {wireTransferLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Génération...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Ordre de Virement
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="border-b border-gray-200 dark:border-gray-700 px-6">
            <TabsList className="grid w-full grid-cols-4 bg-transparent p-0 h-auto">
              <TabsTrigger
                value="payslips"
                className="flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">Bulletins de Paie</span>
              </TabsTrigger>
              <TabsTrigger
                value="expenses"
                className="flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                <Receipt className="h-4 w-4" />
                <span className="hidden sm:inline">Notes de Frais</span>
              </TabsTrigger>
              {currentUser &&
                ["admin", "rh", "manager"].includes(currentUser.role) && (
                  <>
                    <TabsTrigger
                      value="calculator"
                      className="flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    >
                      <Calculator className="h-4 w-4" />
                      <span className="hidden sm:inline">Calculateur</span>
                    </TabsTrigger>
                    <TabsTrigger
                      value="reports"
                      className="flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    >
                      <TrendingUp className="h-4 w-4" />
                      <span className="hidden sm:inline">Rapports</span>
                    </TabsTrigger>
                  </>
                )}
            </TabsList>
          </div>

          <div className="p-6">
            <TabsContent value="payslips" className="space-y-6">
              <PayslipManagement />
            </TabsContent>
            <TabsContent value="expenses" className="space-y-6">
              <ExpenseManagement />
            </TabsContent>
            {currentUser &&
              ["admin", "rh", "manager"].includes(currentUser.role) && (
                <>
                  <TabsContent value="calculator" className="space-y-6">
                    <PayrollCalculator />
                  </TabsContent>
                  <TabsContent value="reports" className="space-y-6">
                    <PayrollReports />
                  </TabsContent>
                </>
              )}
          </div>
        </Tabs>
      </div>
    </div>
  );
}
